import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";
import { ImageElement } from "../Components/Elements/Graphics/ImageElement";
import { AnimationElement } from "../Components/Elements/Graphics/AnimationElement";
import { CustomUtils } from "../Utils/CustomUtils";

/**
 * Container element to simulate PNG-based (full transparency) ImageElement via a pair of ImageElements/AnimationElements -
 * on the bottom, a full transparency PNG mask (black only with alpha channel); above it a no transparency JPEG
 * version of the original ImageElement with additive blend mode.
 * The aim of ImageGroupElement is to simplify configuration and improve animation performance.
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ImageGroupElement extends BaseElement {
    // full transparency black PNG mask ImageElement/AnimationElement
    maskElement: ImageElement | AnimationElement;
    // no transparency additive ImageElement/AnimationElement
    imageElement: ImageElement | AnimationElement;

    _tint: number = 0xffffff;
    blendMode: number = 0;

    constructor() {
        super();
    }

    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        if (this.maskElement) {
            this.removeChild(this.maskElement);
            this.maskElement.destroy();
            this.maskElement = null;
        }
        if (this.imageElement) {
            this.removeChild(this.imageElement);
            this.imageElement.destroy();
            this.imageElement = null;
        }
    }

    get tint(): number {
        return this._tint;
    }

    set tint(tintColor: number) {
        this._tint = tintColor;
        if (this.imageElement) {
            this.imageElement.tint = this._tint;
        }
        if (this.maskElement) {
            this.maskElement.tint = this._tint;
        }
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    public createByNode(node: Element): void {
        super.createByNode(node);
        this.updateByNode(node);
    }

    public updateByNode(node: Element): void {
        super.updateByNode(node);

        if (node.hasAttribute(ImageGroupElementAttributes.BLEND_MODE)) {
            this.blendMode = CustomUtils.getBlendMode(
                parseInt(node.getAttribute(ImageGroupElementAttributes.BLEND_MODE)),
            );
        }

        let imageName: string = null;

        let imageNode: Element = this.parseChild(node, "IMAGE_ELEMENT");
        if (imageNode != null) {
            if (imageNode.hasAttribute("Image")) {
                imageName = imageNode.getAttribute("Image");
            }
            if (imageNode.hasAttribute("Image") || imageNode.hasAttribute("Style")) {
                if (this.imageElement) {
                    this.removeChild(this.imageElement);
                    this.imageElement.destroy();
                    this.imageElement = null;
                }
            }
            imageNode.setAttribute("BlendMode", "1");
            if (this.imageElement) {
                this.imageElement.updateByNode(imageNode);
            } else {
                switch (imageNode.getAttribute("Class")) {
                    case "ImageElement":
                        this.imageElement = new ImageElement();
                        this.imageElement.create(imageNode);
                        this.addChild(this.imageElement);
                        break;
                    case "AnimationElement":
                        this.imageElement = new AnimationElement();
                        this.imageElement.create(imageNode);
                        this.addChild(this.imageElement);
                        break;
                }
            }
        }

        let maskNode: Element = this.parseChild(node, "MASK_ELEMENT");
        if (maskNode != null) {
            if (maskNode.hasAttribute("Image")) {
                // shorthand: <... ImageElement="GFX_image" MaskImage="" ...> results in <... ImageElement="GFX_image" MaskImage="GFX_image_mask" ...>
                switch (maskNode.getAttribute("Image")) {
                    case "_":
                    case "":
                    case "default":
                        if (imageName) {
                            maskNode.setAttribute("Image", imageName + "_mask");
                        }
                        break;
                }
            }
            if (maskNode.hasAttribute("Image") || maskNode.hasAttribute("Style")) {
                if (this.maskElement) {
                    this.removeChild(this.maskElement);
                    this.maskElement.destroy();
                    this.maskElement = null;
                }
            }
            if (this.maskElement) {
                this.maskElement.updateByNode(maskNode);
            } else {
                switch (maskNode.getAttribute("Class")) {
                    case "ImageElement":
                        this.maskElement = new ImageElement();
                        this.maskElement.create(maskNode);
                        this.addChildAt(this.maskElement, 0);
                        break;
                    case "AnimationElement":
                        this.maskElement = new AnimationElement();
                        this.maskElement.create(maskNode);
                        this.addChildAt(this.maskElement, 0);
                        break;
                }
            }
        }

        if (node.hasAttribute(ImageGroupElementAttributes.TINT)) {
            this.tint = parseInt(node.getAttribute(ImageGroupElementAttributes.TINT), 16);
        }
        if (
            node.hasAttribute(ImageGroupElementAttributes.STOP) ||
            node.hasAttribute(ImageGroupElementAttributes.PLAY) ||
            node.hasAttribute(ImageGroupElementAttributes.REVERSE)
        ) {
            if (this.imageElement instanceof AnimationElement || this.maskElement instanceof AnimationElement) {
                let tempNode: Element = document.createElement("UpdateNode");
                if (node.hasAttribute(ImageGroupElementAttributes.STOP)) {
                    tempNode.setAttribute(
                        ImageGroupElementAttributes.STOP,
                        node.getAttribute(ImageGroupElementAttributes.STOP),
                    );
                }
                if (node.hasAttribute(ImageGroupElementAttributes.PLAY)) {
                    tempNode.setAttribute(
                        ImageGroupElementAttributes.PLAY,
                        node.getAttribute(ImageGroupElementAttributes.PLAY),
                    );
                }
                if (node.hasAttribute(ImageGroupElementAttributes.REVERSE)) {
                    tempNode.setAttribute(
                        ImageGroupElementAttributes.REVERSE,
                        node.getAttribute(ImageGroupElementAttributes.REVERSE),
                    );
                }
                if (this.imageElement instanceof AnimationElement) {
                    this.imageElement.updateByNode(tempNode);
                }
                if (this.maskElement instanceof AnimationElement) {
                    this.maskElement.updateByNode(tempNode);
                }
            }
        }

        switch (this.blendMode) {
            case 0:
                if (this.imageElement) {
                    this.imageElement.blendMode = CustomUtils.getBlendMode(1);
                }
                if (this.maskElement) {
                    this.maskElement.blendMode = CustomUtils.getBlendMode(this.blendMode);
                }
                break;
            default:
                if (this.imageElement) {
                    this.imageElement.blendMode = CustomUtils.getBlendMode(this.blendMode);
                }
                if (this.maskElement) {
                    this.maskElement.blendMode = CustomUtils.getBlendMode(this.blendMode);
                }
                break;
        }
    }

    private parseChild(node: Element, keyPrefix: string = "MASK_ELEMENT"): Element {
        let tempNode: Element = null;
        let attributePrefix: string = ImageGroupElementAttributes[keyPrefix];
        if (!attributePrefix) {
            if (DEBUG) {
                this.log("Unknown key prefix: '" + keyPrefix + "'");
            }
            return;
        }
        if (node.hasAttribute(ImageGroupElementAttributes[keyPrefix])) {
            if (tempNode == null) {
                tempNode = document.createElement(attributePrefix + "Node");
            }
            tempNode.setAttribute("Class", "ImageElement");
            tempNode.setAttribute("Image", node.getAttribute(ImageGroupElementAttributes[keyPrefix]));
        }
        if (node.hasAttribute(ImageGroupElementAttributes[keyPrefix + "_STYLE"])) {
            if (tempNode == null) {
                tempNode = document.createElement(attributePrefix + "Node");
            }
            tempNode.setAttribute("Class", "AnimationElement");
            tempNode.setAttribute("Style", node.getAttribute(ImageGroupElementAttributes[keyPrefix + "_STYLE"]));
        }
        for (let key in ImageGroupElementAttributes) {
            if (key.substring(0, 5) == "ATTR_") {
                let attributeName = ImageGroupElementAttributes[key];
                if (node.hasAttribute(attributePrefix + attributeName)) {
                    if (tempNode == null) {
                        tempNode = document.createElement(attributePrefix + "Node");
                    }
                    tempNode.setAttribute(attributeName, node.getAttribute(attributePrefix + attributeName));
                }
            }
        }
        return tempNode;
    }

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return ContainerElement
     */
    clone(): ImageGroupElement {
        let element: ImageGroupElement = new ImageGroupElement();

        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.skew.x = this.skew.x;
        element.skew.y = this.skew.y;
        element.visible = this.visible;
        element.name = this.name;

        element.removeChildren();

        if (this.maskElement) {
            element.maskElement = this.maskElement.clone();
            element.addChild(element.maskElement);
        }
        if (this.imageElement) {
            element.imageElement = this.imageElement.clone();
            element.addChild(element.imageElement);
        }

        element.markAsCloned(); // must be called last before return!
        return element;
    }
}

const ImageGroupElementAttributes = {
    DEBUG: "Debug",

    MASK_ELEMENT: "MaskElement",
    MASK_ELEMENT_STYLE: "MaskElementStyle",
    IMAGE_ELEMENT: "ImageElement",
    IMAGE_ELEMENT_STYLE: "ImageElementStyle",

    ATTR_CLASS: "Class",
    ATTR_PIVOT_X: "PivotX",
    ATTR_PIVOT_Y: "PivotY",
    ATTR_POSITION_X: "PositionX",
    ATTR_POSITION_Y: "PositionY",
    ATTR_ROTATION: "Rotation",
    ATTR_SCALE: "Scale",
    ATTR_SCALE_X: "ScaleX",
    ATTR_SCALE_Y: "ScaleY",
    ATTR_SKEW_X: "SkewX",
    ATTR_SKEW_Y: "SkewY",
    ATTR_X: "X",
    ATTR_Y: "Y",

    BLEND_MODE: "BlendMode",

    PLAY: "Play",
    REVERSE: "Reverse",
    STOP: "Stop",

    TINT: "Tint",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ImageGroupElement.ts
 **/